import { extend } from './utils/extend.js';
import { isString } from './utils/isString.js';
import { isUndefined } from './utils/isUndefined.js';
import { isElement } from './utils/isElement.js';
import { isFunction } from './utils/isFunction.js';
import { isWindow } from './utils/isWindow.js';
import { JsonRpcRequest } from './jsonrpc/request.js';
import { JsonRpcNotification } from './jsonrpc/notification.js';
import { JsonRpcResponse } from './jsonrpc/response.js';
import { JsonRpcError } from './jsonrpc/error.js';

var eventMessage = 'message';
/**
 * WindowMessenger
 * @constructor
 * @param {HTMLIFrameElement|Window} recipient - The window to communicate with
 * @param {String} recipientOrigin - The origin of the window to communicate with
 * @param {String} [channelId] - A unique channel identifier. If passed, it will be included in every message as "channel".
 * @param {String} [name] - A descriptive name to identify the WindowMessenger instance. For debugging purposes.
 * @param {Boolean} [isLoggingEnabled] - True false whether debug logging is enabled
 */
var WindowMessenger = /** @class */ (function () {
    function WindowMessenger(recipient, recipientOrigin, channelId, name, isLoggingEnabled) {
        var _this = this;
        /**
         * @this {WindowMessenger} - Current instance
         * @param {MessageEvent} event
         */
        this.onMessageReceived = function (event) {
            var eventOrigin = event.origin;
            _this.log('Message received', event);
            if (eventOrigin !== _this.recipientOrigin) {
                //Message received from unwanted / untrusted origin, skip
                _this.log('Ignoring message received from', eventOrigin, event);
                return;
            }
            var data = event.data;
            var id = data.id;
            var responseIsExpected = !isUndefined(id) && id !== null;
            var callback = _this.callbackStore[id];
            //If a reply from the other end, call the callback handler
            if (isFunction(callback)) {
                callback(data.error ? null : data.result);
            }
            else {
                var method = data.method;
                var payload = data.params;
                if (isFunction(_this.methodStore[method])) {
                    _this.methodStore[method](payload, onMethodExecuted, onMethodError);
                }
                else {
                    _this.log('No handler defined for method', method, payload);
                    onMethodError('No handler defined');
                }
            }
            function onMethodExecuted(result) {
                if (responseIsExpected) {
                    // @ts-ignore
                    event.source.postMessage(new JsonRpcResponse(result, null, id), eventOrigin);
                }
            }
            function onMethodError(error) {
                if (responseIsExpected) {
                    // @ts-ignore
                    event.source.postMessage(new JsonRpcError(-32000, error, {
                        id: id
                    }), eventOrigin);
                }
            }
        };
        this.callbackStore = {};
        this.channel = channelId;
        this.methodStore = {};
        this.name = name;
        this.recipient = recipient;
        this.recipientWindow = null;
        this.recipientOrigin = recipientOrigin;
        this.isLoggingEnabled = !!isLoggingEnabled;
        this.log('Messenger created on channel', this.channel);
    }
    WindowMessenger.prototype.init = function () {
        window.addEventListener(eventMessage, this.onMessageReceived, false);
        this.log('Messenger listening on channel', this.channel);
    };
    WindowMessenger.prototype.send = function (method, params, callback) {
        var message;
        if (isFunction(params)) {
            callback = params;
            params = undefined;
        }
        var payload = {
            channel: this.channel,
            params: params
        };
        this.log('Sending message', method, payload);
        if (isFunction(callback)) {
            // @ts-ignore
            message = new JsonRpcRequest(method, payload);
            this.callbackStore[message.id] = callback.bind(message);
        }
        else {
            // @ts-ignore
            message = new JsonRpcNotification(method, payload);
        }
        this.getRecipientWindow().postMessage(message, this.recipientOrigin);
        return this;
    };
    WindowMessenger.prototype.register = function (nameOrMap, handler) {
        if (!nameOrMap) {
            throw new TypeError('The ’nameOrMap’ must be a string or a map');
        }
        this.log('Registering method(s)', nameOrMap, handler);
        if (isString(nameOrMap)) {
            this.methodStore[nameOrMap] = handler;
        }
        else {
            extend(this.methodStore, nameOrMap);
        }
        return this;
    };
    WindowMessenger.prototype.destroy = function () {
        this.callbackStore = {};
        this.methodStore = {};
        window.removeEventListener(eventMessage, this.onMessageReceived);
        this.log('Destroyed!');
        return this;
    };
    WindowMessenger.prototype.getRecipientWindow = function () {
        if (this.recipientWindow) {
            return this.recipientWindow;
        }
        if (isWindow(this.recipient)) {
            this.recipientWindow = this.recipient;
        }
        else if (isElement('iframe', this.recipient)) {
            this.recipientWindow = this.recipient.contentWindow;
        }
        else {
            throw new TypeError('WindowMessenger recipient has to be a window object or an <iframe>-element');
        }
        return this.recipientWindow;
    };
    WindowMessenger.prototype.log = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (this.isLoggingEnabled) {
            var prefix = ['[' + this.name + ']:'];
            // eslint-disable-next-line no-console
            console.log.apply(console, prefix.concat(args));
        }
    };
    return WindowMessenger;
}());

export { WindowMessenger };
